import { useEffect, useMemo, useRef, useState } from "react"
import {
  CalendarBlank,
  CaretLeft,
  CaretRight,
  WarningCircle,
  X,
  ArrowCounterClockwise,
} from "@phosphor-icons/react"
import { format, isValid, parse } from "date-fns"
import { type DateRange } from "react-day-picker"

import {
  calendarDefaultMinDate,
  calendarDefaultMaxDate,
  months,
  convertToStartOfWeekUTC,
} from "~/utils/datetime"
import { Calendar } from "~/components/reusable/Calendar"
import { Button, IconButton } from "~/components/reusable/Button"
import { inputVariants } from "~/components/reusable/Input"
import { Tooltip } from "~/components/reusable/Tooltip"
import { Separator } from "~/components/reusable/Separator"
import { Popover, PopoverAnchor, PopoverContent } from "~/components/reusable/Popover"
import { cn } from "~/utils/helpers"
import { toValidDate } from "~/components/reusable/DatePicker.helpers"

export type CalendarView = "day" | "week" | "month" | "quarter"

export type GridItem = { label: string; startMonth: number; spanMonths: number }

export const MONTH_ITEMS: GridItem[] = months.map((label, i) => ({
  label,
  startMonth: i,
  spanMonths: 1,
}))
const QUARTER_ITEMS: GridItem[] = [
  { label: "Q1", startMonth: 0, spanMonths: 3 },
  { label: "Q2", startMonth: 3, spanMonths: 3 },
  { label: "Q3", startMonth: 6, spanMonths: 3 },
  { label: "Q4", startMonth: 9, spanMonths: 3 },
]
const VIEW_LABELS: Record<CalendarView, string> = {
  day: "Day",
  week: "Week",
  month: "Month",
  quarter: "Quarter",
}

const DATE_FORMAT = "dd/MM/yyyy"
const DATE_FORMAT_RE = /^\d{2}\/\d{2}\/\d{4}$/

type ErrorType = "format" | "range"

const ERROR_MESSAGES: Record<ErrorType, { title: string; description?: string }> = {
  format: {
    title: "Invalid date format",
    description: "Enter date as DD/MM/YYYY, for example 25/06/2026",
  },
  range: {
    title: "Date is outside allowed range",
  },
}

const PR_BY_ICON_COUNT = ["pr-8", "pr-14", "pr-20", "pr-24"] as const

/**
 * Re-expresses a min/max bound as local midnight of the calendar day it *displays*.
 *
 * Callers pass bounds either as plain local Dates or as `UTCDate` instances (whose `get*`
 * accessors report UTC), while react-day-picker and the typed input both work in plain local
 * time. Without this, a UTC-midnight bound sits a few hours *after* local midnight of its own
 * day, so every positive-offset viewer sees the boundary day itself rejected as out of range.
 */
const toLocalDayStart = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate())

function YearNav({ year, onYearChange }: { year: number; onYearChange: (y: number) => void }) {
  return (
    <div className="flex items-center justify-between h-7 mb-3">
      <IconButton
        variant="transparent"
        size="sm"
        className="p-1.5 rounded-lg hover:bg-base-hover text-fg-muted"
        onClick={() => onYearChange(year - 1)}
      >
        <CaretLeft className="size-4" />
      </IconButton>
      <span className="text-sm font-medium select-none">{year}</span>
      <IconButton
        variant="transparent"
        size="sm"
        className="p-1.5 rounded-lg hover:bg-base-hover text-fg-muted"
        onClick={() => onYearChange(year + 1)}
      >
        <CaretRight className="size-4" />
      </IconButton>
    </div>
  )
}

export function PeriodGrid({
  items,
  cols,
  year,
  onYearChange,
  selected,
  selectedRange,
  onSelect,
  minDate,
  maxDate,
}: {
  items: GridItem[]
  cols: 2 | 3
  year: number
  onYearChange: (y: number) => void
  selected?: Date
  selectedRange?: DateRange
  onSelect: (date: Date) => void
  minDate: Date
  maxDate: Date
}) {
  return (
    <div className="min-w-[196px]">
      <YearNav year={year} onYearChange={onYearChange} />
      <div className={cn("grid gap-1.5", cols === 3 ? "grid-cols-3" : "grid-cols-2")}>
        {items.map(({ label, startMonth, spanMonths }) => {
          const itemStart = new Date(Date.UTC(year, startMonth, 1))
          const itemEnd = new Date(Date.UTC(year, startMonth + spanMonths, 0))
          const isDisabled = itemEnd < minDate || itemStart > maxDate
          const isSelected =
            selected?.getFullYear() === year &&
            selected.getMonth() >= startMonth &&
            selected.getMonth() < startMonth + spanMonths
          const fromMonth = selectedRange?.from
            ? new Date(Date.UTC(selectedRange.from.getFullYear(), selectedRange.from.getMonth(), 1))
            : undefined
          const toMonth = selectedRange?.to
            ? new Date(Date.UTC(selectedRange.to.getFullYear(), selectedRange.to.getMonth(), 1))
            : undefined
          const isStart = fromMonth?.getTime() === itemStart.getTime()
          const isEnd = toMonth?.getTime() === itemStart.getTime()
          const isInRange = !!(fromMonth && toMonth && itemStart > fromMonth && itemStart < toMonth)
          return (
            <Button
              key={label}
              type="button"
              variant="transparent"
              size="sm"
              disabled={isDisabled}
              onClick={() => onSelect(itemStart)}
              className={cn(
                "justify-center",
                isSelected || isStart || isEnd
                  ? "bg-interactive text-fg-on-primary shadow-button-neutral hover:bg-interactive"
                  : isInRange
                    ? "bg-interactive/15 text-fg-base shadow-button-neutral hover:bg-interactive/15"
                    : "hover:bg-base-hover",
              )}
            >
              {label}
            </Button>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Single-date picker — Input shell trigger + Calendar popover. For ranges use DateRangePicker.
 * Supports day/week/month/quarter views via an optional view-selector sidebar.
 * Canonical intent: {@link ../design-system/intents.ts DATE_PICKER_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
export default function DatePicker({
  onChange,
  value,
  className,
  disabled,
  minDate = calendarDefaultMinDate,
  maxDate = calendarDefaultMaxDate,
  clearable,
  defaultValue,
  filterDate,
  fullWidth,
  withConfirmation = false,
  placeholder = "DD/MM/YYYY",
  enableViewSelector = false,
  viewOptions = ["day", "week", "month", "quarter"],
  defaultView,
  onViewChange,
  insideGridPopup = false,
}: {
  onChange: (date: Date | undefined) => void
  value: Date | undefined
  className?: string
  disabled?: boolean
  minDate?: Date
  maxDate?: Date
  clearable?: boolean
  defaultValue?: Date
  filterDate?: (date: Date) => boolean
  fullWidth?: boolean
  withConfirmation?: boolean
  placeholder?: string
  enableViewSelector?: boolean
  viewOptions?: CalendarView[]
  defaultView?: CalendarView
  onViewChange?: (view: CalendarView) => void
  insideGridPopup?: boolean
}) {
  const safeValue = toValidDate(value)
  const initialView = defaultView ?? viewOptions[0] ?? "day"
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState(safeValue ? format(safeValue, DATE_FORMAT) : "")
  const [pendingDate, setPendingDate] = useState<Date | undefined>(safeValue)
  const [displayMonth, setDisplayMonth] = useState<Date | undefined>(safeValue)
  const [calendarView, setCalendarView] = useState<CalendarView>(initialView)
  // Tracks the last committed view — calendarView may differ while dropdown is open
  const [committedView, setCommittedView] = useState<CalendarView>(initialView)
  const [displayYear, setDisplayYear] = useState<number>((safeValue ?? new Date()).getFullYear())
  const [errorType, setErrorType] = useState<ErrorType | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const isTypingDisabled = enableViewSelector && calendarView !== "day"

  // Sync only when external value actually changes — not on open/close, so invalid
  // typed input and its error stay visible until the user corrects or clears them.
  const lastSyncedValue = useRef(safeValue)
  useEffect(() => {
    const safeV = toValidDate(value)
    if (lastSyncedValue.current?.getTime() === safeV?.getTime()) return
    lastSyncedValue.current = safeV
    setInputValue(safeV ? format(safeV, DATE_FORMAT) : "")
    setPendingDate(safeV)
    setErrorType(null)
  }, [value])

  const minDay = useMemo(() => toLocalDayStart(minDate), [minDate])
  const maxDay = useMemo(() => toLocalDayStart(maxDate), [maxDate])

  const disabledMatcher = useMemo(
    () => [
      { before: minDay },
      { after: maxDay },
      ...(filterDate ? [(d: Date) => !filterDate(d)] : []),
    ],
    [minDay, maxDay, filterDate],
  )

  const resetDropdownState = (nextValue: Date | undefined) => {
    setInputValue(nextValue ? format(nextValue, DATE_FORMAT) : "")
    setPendingDate(nextValue)
    setDisplayMonth(nextValue)
    setDisplayYear((nextValue ?? new Date()).getFullYear())
    setCalendarView(committedView)
    setErrorType(null)
  }

  const commitTypedDate = () => {
    if (isTypingDisabled) return
    if (!inputValue) {
      setErrorType(null)
      if (withConfirmation) {
        setPendingDate(undefined)
      } else {
        onChange(undefined)
      }
      return
    }
    const parsed = parse(inputValue, DATE_FORMAT, new Date())
    if (!DATE_FORMAT_RE.test(inputValue) || !isValid(parsed)) {
      setErrorType("format")
      return
    }
    if (parsed < minDay || parsed > maxDay) {
      setErrorType("range")
      return
    }
    setErrorType(null)
    const utc = new Date(Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()))
    if (withConfirmation) {
      setPendingDate(utc)
    } else {
      onChange(utc)
    }
  }

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      if (withConfirmation) {
        // confirmation mode: close doesn't commit; revert input to pending or value,
        // unless an error is showing — keep the bad input visible so the user can fix it.
        if (!errorType) {
          setInputValue(
            pendingDate
              ? format(pendingDate, DATE_FORMAT)
              : safeValue
                ? format(safeValue, DATE_FORMAT)
                : "",
          )
        }
      } else {
        commitTypedDate()
      }
    }
    setOpen(next)
  }

  const handleViewChange = (view: CalendarView) => {
    setCalendarView(view)
    setPendingDate(undefined)
    if (!withConfirmation) {
      setCommittedView(view)
    }
  }

  const handleGridSelect = (date: Date) => {
    if (withConfirmation) {
      setPendingDate(date)
      setInputValue(format(date, DATE_FORMAT))
      setErrorType(null)
    } else {
      onChange(date)
      onViewChange?.(calendarView)
      setInputValue(format(date, DATE_FORMAT))
      setErrorType(null)
      setOpen(false)
    }
  }

  const handleCalendarSelect = (newDate: Date | undefined) => {
    if (!newDate) return
    const normalized = calendarView === "week" ? convertToStartOfWeekUTC(newDate) : newDate
    const utc = new Date(
      Date.UTC(normalized.getFullYear(), normalized.getMonth(), normalized.getDate()),
    )
    if (withConfirmation) {
      setPendingDate(utc)
      setInputValue(format(utc, DATE_FORMAT))
      setErrorType(null)
    } else {
      onChange(utc)
      onViewChange?.(calendarView)
      setInputValue(format(utc, DATE_FORMAT))
      setErrorType(null)
      setOpen(false)
    }
  }

  const showReset = defaultValue !== undefined && safeValue?.getTime() !== defaultValue.getTime()
  const showClear = clearable && !!safeValue
  const error = errorType ? ERROR_MESSAGES[errorType] : undefined
  const rightIconCount = 1 + (showReset ? 1 : 0) + (showClear ? 1 : 0) + (error ? 1 : 0)
  const prClass = PR_BY_ICON_COUNT[rightIconCount - 1] ?? "pr-8"

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <div ref={containerRef} className={cn("relative", fullWidth && "w-full", className)}>
        <PopoverAnchor asChild>
          <div className="relative">
            <input
              type="text"
              value={inputValue}
              readOnly={isTypingDisabled}
              onChange={(e) => {
                if (isTypingDisabled) return
                setInputValue(e.target.value)
                setErrorType(null)
                const parsed = parse(e.target.value, DATE_FORMAT, new Date())
                if (isValid(parsed)) {
                  setDisplayMonth(parsed)
                  setDisplayYear(parsed.getFullYear())
                }
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  commitTypedDate()
                  if (!withConfirmation) setOpen(false)
                }
                if (e.key === "Escape") {
                  resetDropdownState(safeValue)
                  setOpen(false)
                }
              }}
              placeholder={placeholder}
              disabled={disabled}
              aria-invalid={!!errorType}
              className={cn(
                inputVariants(),
                fullWidth && "w-full",
                prClass,
                errorType && "shadow-borders-error",
              )}
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {error && (
                <Tooltip title={error.title} description={error.description} side="top" align="end">
                  <WarningCircle weight="fill" className="size-4 text-fg-danger" />
                </Tooltip>
              )}
              {showReset && (
                <ArrowCounterClockwise
                  className="cursor-pointer text-fg-muted hover:text-fg"
                  onMouseDown={(e) => {
                    e.preventDefault()
                    setPendingDate(defaultValue)
                    setInputValue(defaultValue ? format(defaultValue, DATE_FORMAT) : "")
                    setErrorType(null)
                    onChange(defaultValue)
                  }}
                />
              )}
              {showClear && (
                <X
                  className="cursor-pointer text-fg-muted hover:text-fg"
                  onMouseDown={(e) => {
                    e.preventDefault()
                    setPendingDate(undefined)
                    setInputValue("")
                    setErrorType(null)
                    onChange(undefined)
                  }}
                />
              )}
              <button
                type="button"
                tabIndex={-1}
                disabled={disabled}
                onClick={() => setOpen((v) => !v)}
                className="text-fg-muted hover:text-fg disabled:cursor-not-allowed"
              >
                <CalendarBlank className="size-4" />
              </button>
            </div>
          </div>
        </PopoverAnchor>

        <PopoverContent
          align="start"
          sideOffset={4}
          // Keep focus on the text input while open so the user can type a date.
          onOpenAutoFocus={(e) => e.preventDefault()}
          // Don't return focus to the input on close — it would re-fire onFocus and reopen.
          onCloseAutoFocus={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => {
            // Radix sees Escape first (document capture). Stopping it here means one Escape only
            // closes the calendar, not a host that also closes on Escape (AG Grid filter popup).
            // It never reaches the input's onKeyDown either, so close and reset here instead.
            e.preventDefault()
            e.stopPropagation()
            handleOpenChange(false)
            resetDropdownState(safeValue)
          }}
          onInteractOutside={(e) => {
            // The text input is the anchor (outside the portaled content) — clicking it
            // shouldn't close the popover.
            if (containerRef.current?.contains(e.target as Node)) e.preventDefault()
          }}
          className={cn("w-auto p-0 flex flex-row", insideGridPopup && "ag-custom-component-popup")}
        >
          <div className="flex flex-col">
            <div className="p-4">
              {calendarView === "month" ? (
                <PeriodGrid
                  items={MONTH_ITEMS}
                  cols={3}
                  year={displayYear}
                  onYearChange={setDisplayYear}
                  selected={pendingDate}
                  onSelect={handleGridSelect}
                  minDate={minDate}
                  maxDate={maxDate}
                />
              ) : calendarView === "quarter" ? (
                <PeriodGrid
                  items={QUARTER_ITEMS}
                  cols={2}
                  year={displayYear}
                  onYearChange={setDisplayYear}
                  selected={pendingDate}
                  onSelect={handleGridSelect}
                  minDate={minDate}
                  maxDate={maxDate}
                />
              ) : (
                <Calendar
                  mode="single"
                  weekStartsOn={1}
                  month={displayMonth}
                  onMonthChange={setDisplayMonth}
                  selected={pendingDate ?? safeValue}
                  onSelect={handleCalendarSelect}
                  disabled={disabledMatcher}
                  showOutsideDays
                  showWeekNumber={calendarView === "week"}
                  captionLayout="dropdown"
                  startMonth={minDate}
                  endMonth={maxDate}
                />
              )}
            </div>
            {withConfirmation && (
              <>
                <Separator orientation="horizontal" />
                <div className="flex flex-row gap-2 p-3 bg-subtle rounded-b-[12px]">
                  <Button
                    type="button"
                    variant="neutral"
                    onMouseDown={(e) => {
                      e.preventDefault()
                      resetDropdownState(safeValue)
                      setOpen(false)
                    }}
                    className="w-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    onMouseDown={(e) => {
                      e.preventDefault()
                      onChange(pendingDate)
                      if (calendarView !== committedView) {
                        setCommittedView(calendarView)
                        onViewChange?.(calendarView)
                      }
                      setInputValue(pendingDate ? format(pendingDate, DATE_FORMAT) : "")
                      setOpen(false)
                    }}
                    className="w-full"
                  >
                    Apply
                  </Button>
                </div>
              </>
            )}
          </div>
          {enableViewSelector && (
            <div className="p-3 min-w-[100px] bg-subtle rounded-r-[12px] border-l border-border-base">
              <ul className="flex flex-col">
                {viewOptions.map((view) => (
                  <li
                    key={view}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      handleViewChange(view)
                    }}
                    className={cn(
                      "px-2 py-1.5 cursor-pointer rounded-md label-base text-fg-subtle hover:bg-base-hover",
                      calendarView === view && "bg-base-hover font-medium",
                    )}
                  >
                    {VIEW_LABELS[view]}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </PopoverContent>
      </div>
    </Popover>
  )
}
